import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { loadModules } from 'esri-loader';
import { useAuth } from '../../../../AuthProvider';
import { addUnavailableNotice, applyDiscernShading, removeRetiredLayers } from '../../../../utils/discernWebmapShading';
import ExpansionBlock from '../../../CustomVestmapViewer/components/ExpansionBlock';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';

function ExpansionMap({ webmapID, token, addressInfo }) {
  const { getAccessToken } = useAuth();
  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!webmapID || !token || !addressInfo) return;

    let view;
    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/WebMap',
      'esri/geometry/Extent',
      'esri/Graphic',
      'esri/widgets/Legend',
      'esri/config',
      'esri/layers/FeatureLayer',
    ]).then(
      ([Map, MapView, WebMap, Extent, Graphic, Legend, esriConfig, FeatureLayer]) => {
        if (!webmapID || !token) return;

        esriConfig.apiKey = token;

        const extent = new Extent({
          xmin: addressInfo.address_xmin,
          ymin: addressInfo.address_ymin,
          xmax: addressInfo.address_xmax,
          ymax: addressInfo.address_ymax,
          spatialReference: {
            wkid: addressInfo.spatialReference?.wkid || 4326,
          },
        });

        const webmap = new WebMap({
          portalItem: {
            id: webmapID,
          },
        });

        // Redraw the shaded layer from 2026 GeoEnrichment values (flagged; any
        // failure or signed-out view keeps the authored 2024 layer).
        webmap.when(() => {
          removeRetiredLayers(webmap);
          applyDiscernShading({
            webmap,
            addressInfo,
            getAccessToken,
            deps: { FeatureLayer, Graphic },
            isStale: () => view?.destroyed === true || webmap?.destroyed === true,
            onUnavailable: () => addUnavailableNotice(view),
          });
        });

        view = new MapView({
          container: mapRef.current,
          map: webmap,
          extent: extent,
          zoom: 14,
          navigation: {
            mouseWheelZoomEnabled: false,
          },
        });

        viewRef.current = view;

        view
          .when(() => {
            setIsMapLoaded(true);
            setErrorMessage(null);

            view.ui.add(
              new Legend({
                view: view,
              }),
              'top-right',
            );

            if (
              addressInfo.address_longitude &&
              addressInfo.address_latitude
            ) {
              const customSymbol = {
                type: 'picture-marker',
                url: marker,
                width: '50px',
                height: '100px',
              };

              view.graphics.add(
                new Graphic({
                  geometry: {
                    type: 'point',
                    longitude: addressInfo.address_longitude,
                    latitude: addressInfo.address_latitude,
                  },
                  symbol: customSymbol,
                }),
              );
            }
          })
          .catch(err => {
            console.error('Error loading expansion map:', err);
            setIsMapLoaded(false);
            setErrorMessage(err.message || 'Failed to load map');
          });
      },
    );

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, [webmapID, token, addressInfo]);

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          sm: '30rem',
          xs: '20rem',
        },
        position: 'relative',
      }}
    >
      {!isMapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {errorMessage ? (
            <Typography
              variant='body2'
              sx={{ color: '#b00020', textAlign: 'center', p: 2 }}
            >
              {errorMessage}
            </Typography>
          ) : (
            <CircularProgress sx={{ color: '#4FA490' }} />
          )}
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      />
    </Box>
  );
}

const ExpansionCard = ({ type, addressInfo, expansionData, webmapID, arcgisToken, expansionLoading = false }) => {
  if (type === 'map') {
    if (expansionLoading && !addressInfo) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <CircularProgress sx={{ color: '#4FA490' }} />
        </Box>
      );
    }
    if (!webmapID || !arcgisToken || !addressInfo) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view expansion map
          </Typography>
        </Box>
      );
    }

    return <ExpansionMap webmapID={webmapID} token={arcgisToken} addressInfo={addressInfo} />;
  }

  if (type === 'data') {
    if (expansionLoading && (!expansionData || Object.keys(expansionData).length === 0)) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <CircularProgress sx={{ color: '#4FA490' }} />
        </Box>
      );
    }
    if (!expansionData || Object.keys(expansionData).length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view expansion data
          </Typography>
        </Box>
      );
    }

    const blocks = [];
    const expansionLevels = ['block', 'tract', 'zip', 'county', 'state', 'national'];

    expansionLevels.forEach(level => {
      const raw = expansionData[level];
      if (raw !== undefined && raw !== null && raw !== '') {
        const numVal = Number(raw);
        const value = Number.isNaN(numVal) ? raw : numVal;
        blocks.push(
          <ExpansionBlock
            key={level}
            sectionBlock={{
              title: level.charAt(0).toUpperCase() + level.slice(1),
              value: { [level]: value },
              outfields: [level],
              key: 'expansion',
            }}
            tooltipTitle={`${level.charAt(0).toUpperCase() + level.slice(1)} Expansion`}
            hideTooltips
          />,
        );
      }
    });

    if (blocks.length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            No expansion data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {blocks.map(block => block)}
      </Box>
    );
  }

  return null;
};

export default ExpansionCard;

